import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, interval } from 'rxjs';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import packageInfo from '../../../../package.json';

@Injectable({
    providedIn: 'root',
})
export class ServiceWorkerService {
    private deployingNewVersion = false;
    public readonly currentVersion = (packageInfo as { version: string }).version;

    constructor(
        private swUpdate: SwUpdate,
        private router: Router,
        private messageService: MessageService,
    ) {
        if (swUpdate.isEnabled) {
            const intervalTime = 1 * 60 * 1000; // 1 min
            interval(intervalTime).subscribe(() =>
                swUpdate.checkForUpdate().catch((error) => {
                    console.error('Error checking for updates:', error);
                }),
            );

            swUpdate.unrecoverable.subscribe((event) => {
                console.error('Service worker unrecoverable state:', event);
                this.messageService.add({
                    severity: 'error',
                    summary: 'App Update Required',
                    detail: 'The app needs to be reloaded to continue working properly.',
                    sticky: true,
                    key: 'unrecoverable-state',
                });
                setTimeout(() => {
                    void this.refresh();
                }, 3000);
            });
        } else {
            console.log('Service Worker is not enabled in this browser.');
        }
    }

    public checkForUpdates(): void {
        if (!this.swUpdate.isEnabled) {
            console.log('Service Worker updates are disabled.');
            return;
        }

        this.swUpdate.versionUpdates
            .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
            .subscribe(() => {
                if (!this.deployingNewVersion) {
                    this.deployingNewVersion = true;

                    if (this.router.url.includes('/auth')) {
                        void this.refresh();
                    } else {
                        this.notifyUpdateAvailable();
                    }
                }
            });
    }

    async refresh(): Promise<void> {
        try {
            await this.swUpdate.activateUpdate();
            this.deployingNewVersion = false;
            this.reloadWindow();
        } catch (error) {
            console.error('Error activating update:', error);
            this.deployingNewVersion = false;
            this.reloadWindow();
        }
    }

    private reloadWindow(): void {
        window.location.reload();
    }

    public manualCheckForUpdate(): Promise<boolean> {
        if (this.swUpdate.isEnabled) {
            return this.swUpdate.checkForUpdate();
        }
        return Promise.resolve(false);
    }

    private notifyUpdateAvailable(): void {
        this.messageService.add({
            severity: 'info',
            summary: 'Update Available',
            detail: 'A new version of the app is available. You can update now or continue and update later.',
            sticky: true,
            key: 'update-available',
        });

        window.dispatchEvent(
            new CustomEvent('sw-update-available', {
                detail: {
                    message: 'A new version of the app is available.',
                },
            }),
        );
    }
}
